import {
  DocumentIcon,
  PhotoIcon,
  BookOpenIcon,
  XMarkIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import { cleanBookTitle } from '../../Homepage/Contexts/DiffitBooksContext';
import { TemplateChip } from '../TemplateChip';
import { useUnlimitedContext } from '../contexts/UnlimitedContext';

/**
 * Renders removable chip indicators for all attachments (PDFs, images, books, links, templates)
 * in the input area. Reads all state from UnlimitedContext.
 */
export const AttachmentChips = () => {
  const { book, template, attachments } = useUnlimitedContext();
  const { selectedBook, startChapter, endChapter, clearBook } = book;
  const { selectedTemplate, clearTemplate } = template;

  const {
    pdfFile,
    isUploadingPdf,
    clearPdf,
    imageFiles,
    uploadedImageURLs,
    isUploadingImage,
    clearImageWithAnalyses,
    attachedLinks,
    clearLink,
  } = attachments;

  const hasAttachments =
    pdfFile ||
    imageFiles.length > 0 ||
    selectedBook ||
    selectedTemplate ||
    attachedLinks.length > 0;

  if (!hasAttachments) return null;

  return (
    <div className='px-4 pt-3 pb-1 flex flex-wrap gap-2'>
      {/* Template Chip */}
      {selectedTemplate && <TemplateChip template={selectedTemplate} onRemove={clearTemplate} />}

      {/* PDF Chip */}
      {pdfFile && (
        <div className='inline-flex items-center gap-2 pl-3 pr-1.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg max-w-full'>
          <DocumentIcon className='w-4 h-4 text-gray-500 flex-shrink-0' />
          <span className='text-sm text-gray-700 truncate max-w-[180px]'>{pdfFile.name}</span>
          {isUploadingPdf ? (
            <span className='text-sm text-gray-500'>Uploading...</span>
          ) : (
            <button
              onClick={clearPdf}
              className='p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors'
              aria-label='Remove PDF'>
              <XMarkIcon className='w-4 h-4' />
            </button>
          )}
        </div>
      )}

      {/* Image Chips */}
      {imageFiles.map((file, index) => (
        <div
          key={`image-${index}`}
          className='inline-flex items-center gap-2 pl-3 pr-1.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg max-w-full'>
          <PhotoIcon className='w-4 h-4 text-gray-500 flex-shrink-0' />
          <span className='text-sm text-gray-700 truncate max-w-[180px]'>{file.name}</span>
          {isUploadingImage && index === imageFiles.length - 1 && !uploadedImageURLs[index] ? (
            <span className='text-sm text-gray-500'>Uploading...</span>
          ) : (
            <button
              onClick={() => clearImageWithAnalyses(index)}
              className='p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors'
              aria-label={`Remove image ${file.name}`}>
              <XMarkIcon className='w-4 h-4' />
            </button>
          )}
        </div>
      ))}

      {/* Book Chip */}
      {selectedBook && (
        <div className='inline-flex items-center gap-2 pl-3 pr-1.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg max-w-full'>
          <BookOpenIcon className='w-4 h-4 text-gray-500 flex-shrink-0' />
          <span className='flex items-baseline gap-1.5'>
            <span className='text-sm text-gray-700 truncate max-w-[180px]'>
              {cleanBookTitle(selectedBook.title)}
            </span>
            <span className='text-xs text-gray-500'>
              Ch. {startChapter + 1}
              {endChapter !== startChapter && `-${endChapter + 1}`}
            </span>
          </span>
          <button
            onClick={clearBook}
            className='p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors'
            aria-label='Remove book'>
            <XMarkIcon className='w-4 h-4' />
          </button>
        </div>
      )}

      {/* Link Chips */}
      {attachedLinks.map((url, index) => (
        <div
          key={`link-${index}`}
          className='inline-flex items-center gap-2 pl-3 pr-1.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg max-w-full'>
          <LinkIcon className='w-4 h-4 text-gray-500 flex-shrink-0' />
          <span className='text-sm text-gray-700 truncate max-w-[180px]'>
            {(() => {
              try {
                return new URL(url).hostname.replace(/^www\./, '');
              } catch {
                return url.length > 30 ? `${url.slice(0, 30)}...` : url;
              }
            })()}
          </span>
          <button
            onClick={() => clearLink(index)}
            className='p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors'
            aria-label='Remove link'>
            <XMarkIcon className='w-4 h-4' />
          </button>
        </div>
      ))}
    </div>
  );
};
